import { Link } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DetailPageHeader } from '@/components/common/DetailPageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import type { JobDetailDto, JobStatus } from '../types';

interface JobHeaderProps {
  job: JobDetailDto;
  onStatusChange: (newStatus: JobStatus) => void;
  onEditClick: () => void;
}

export function JobHeader({ job, onStatusChange, onEditClick }: JobHeaderProps) {
  const getStatusVariant = (status: JobStatus) => {
    switch (status) {
      case 'Active':
        return 'active' as const;
      case 'Collections':
        return 'warning' as const;
      case 'NoGo':
        return 'error' as const;
      case 'Closed':
        return 'purple' as const;
      default:
        return 'inactive' as const;
    }
  };

  const getValidTransitions = (currentStatus: JobStatus): JobStatus[] => {
    switch (currentStatus) {
      case 'Active':
        return ['Closed', 'Collections', 'NoGo'];
      case 'Closed':
        return ['Active', 'Collections'];
      case 'Collections':
        return ['Closed', 'Active', 'NoGo'];
      case 'NoGo':
        return ['Active'];
      default:
        return [];
    }
  };

  const validTransitions = getValidTransitions(job.status);

  return (
    <DetailPageHeader
      left={
        <>
          <h1 className="text-2xl font-bold text-[#0A0A9E]">{job.jobNumber}</h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <StatusBadge
                variant={getStatusVariant(job.status)}
                className="cursor-pointer text-sm flex items-center gap-1"
                aria-label={`Change status. Current status: ${job.status}`}
              >
                {job.status}
                <ChevronDown className="h-3 w-3" />
              </StatusBadge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white">
              {validTransitions.map((status) => (
                <DropdownMenuItem
                  key={status}
                  onClick={() => onStatusChange(status)}
                  className="cursor-pointer"
                >
                  Change to {status}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
      middle={
        <Link
          to={`/leads/${job.leadId}`}
          className="text-lg font-semibold text-[#0A0A9E] hover:underline"
        >
          {job.lead.customerName}
        </Link>
      }
      right={
        <Button
          onClick={onEditClick}
          variant="outline"
          size="sm"
          className="rounded-[50px]"
        >
          Edit
        </Button>
      }
    />
  );
}
